import React, { useMemo, useState, useEffect } from "react";

const BTC_DECIMALS = 8;

/* ===============================
   HELPERS (UNCHANGED)
================================ */
const normalizeBtc = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return Number(n.toFixed(BTC_DECIMALS));
};

const normalizeUsd = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return Number(n.toFixed(2));
};

const formatBtc = (value, decimals = 8) => {
  if (value === null || value === undefined || value === "") return "0";
  return Number(value).toFixed(decimals).replace(/\.?0+$/, "");
};

const BackChevron = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
    aria-label="Back"
  >
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  </button>
);

export default function BuyOrder({
  open,
  offer,
  buyAmount,
  setBuyAmount,
  creatingOrder,
  paymentDetails,
  selectedPaymentType,
  setSelectedPaymentType,
  onClose,
  onSubmit,
  btcUsd,
  onCreateUsdtOrder,
}) {
  const isUSDT = offer?.asset?.toUpperCase() === "USDT";
  const isBTC = offer?.asset?.toUpperCase() === "BTC";
  const requiresPayment = offer?.asset?.toUpperCase() === "USWD";

  const [usdInput, setUsdInput] = useState("");
  const [isTypingUsd, setIsTypingUsd] = useState(false);

  /* =========================================
     USDT SETTLEMENT ASSET
  ========================================= */

  const [settlementAsset, setSettlementAsset] =
    useState("USWD");

  /*
   * view: "form" | "confirm"
   * Mirrors BuyUswd's flow: fill the form, preview/confirm the
   * order details, then actually complete payment only once
   * confirmed.
   */
  const [view, setView] = useState("form");

  // Reset back to the form whenever the modal (re)opens or the
  // offer being purchased changes.
  useEffect(() => {
    if (open) setView("form");
  }, [open, offer?.adId]);

  const selectedPayment = paymentDetails?.find(
    (p) => p.type === selectedPaymentType
  );

  const globalInstruction = selectedPayment?.instruction || "";
  const btcValue = buyAmount || "";

  const derivedUsd = useMemo(() => {
    if (!btcUsd || !btcValue) return "0.00";
    return normalizeUsd(Number(btcValue) * btcUsd);
  }, [btcValue, btcUsd]);

  useEffect(() => {
    if (!isTypingUsd) setUsdInput("");
  }, [btcValue, isTypingUsd]);

  /* ===============================
     AMOUNT LIMIT VALIDATION (NEW)
  ================================ */
  const amountError = useMemo(() => {
    if (!offer) return "";
    const amt = Number(buyAmount);
    if (buyAmount === "" || buyAmount === null || buyAmount === undefined) return "";
    if (Number.isNaN(amt)) return "";

    const min = Number(offer.minLimit);
    const max = Number(offer.maxLimit);

    if (!Number.isNaN(max) && amt > max) {
      const maxLabel = isBTC ? formatBtc(max) : max.toLocaleString();
      return `Amount exceeds the max limit of ${maxLabel} ${offer.asset || ""}`.trim();
    }
    if (!Number.isNaN(min) && amt > 0 && amt < min) {
      const minLabel = isBTC ? formatBtc(min) : min.toLocaleString();
      return `Amount is below the min limit of ${minLabel} ${offer.asset || ""}`.trim();
    }
    return "";
  }, [buyAmount, offer, isBTC]);

  if (!open || !offer) return null;

  /* ===============================
     USDT — derived totals
  ================================ */
  const usdtAmount = Number(buyAmount) || 0;
  const usdtRate = Number(offer?.rate) || 0;

  const usdtSubtotal = usdtAmount * usdtRate;

  const NETWORK_FEE_USWD = 0.01;
  const NETWORK_FEE_GHSX = 0.5;

  const networkFee =
    settlementAsset === "GHSX"
      ? NETWORK_FEE_GHSX
      : NETWORK_FEE_USWD;

  const usdtGrandTotal =
    usdtSubtotal + networkFee;

  /* ===============================
     NON-USDT — derived totals
  ================================ */
  const genericAmount = Number(buyAmount) || 0;
  const genericTotal =
    buyAmount && offer ? Number(buyAmount) * Number(offer.rate) : 0;

  /* ===============================
     PREVIEW / CONFIRM GATING
  ================================ */
  const canPreviewUsdt =
    !creatingOrder &&
    usdtAmount > 0 &&
    !amountError &&
    usdtAmount >= Number(offer.minLimit) &&
    usdtAmount <= Number(offer.maxLimit);

  const canPreviewGeneric =
    !creatingOrder &&
    buyAmount !== "" &&
    !amountError &&
    (!requiresPayment || Boolean(selectedPaymentType));

  const handlePreviewUsdt = (e) => {
    e.preventDefault();
    if (!canPreviewUsdt) return;
    setView("confirm");
  };

  const handlePreviewGeneric = (e) => {
    e.preventDefault();
    if (!canPreviewGeneric) return;
    setView("confirm");
  };

  const handleBackToForm = () => setView("form");

  const handleConfirmUsdt = () => {
    if (onCreateUsdtOrder) {
      onCreateUsdtOrder({
        adId: offer.adId,
        sellerChatId: offer.sellerChatId,
        usdtAmount,
        settlementAsset,
      });
    }
  };

  const handleConfirmGeneric = () => {
    onSubmit({ preventDefault: () => {} });
  };

  return (
    <div
      className="
        fixed inset-0 z-50
        flex
        items-end md:items-center
        justify-center
        bg-black/40
      "
    >
      <div
        className="
          w-full
          h-full
          md:h-auto
          md:max-h-[90vh]
          md:max-w-md

          overflow-hidden

          rounded-none
          md:rounded-xl

          bg-white
          shadow-lg

          dark:bg-neutral-800
        "
      >
               {/* ================= HEADER ================= */}
        <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
          <BackChevron onClick={view === "confirm" ? handleBackToForm : onClose} />
          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            {view === "confirm"
              ? "Confirm Order"
              : isUSDT
                ? "Buy USDT"
                : "Place Order"}
          </h3>
        </div>

        {/* ================= BODY ================= */}
        <div
          className="
            space-y-4
            p-4
            overflow-y-auto
            max-h-[calc(90vh-64px)]
          "
        >

          {view === "confirm" ? (
            <>
              {/* ===================================================
                  CONFIRM / PREVIEW SCREEN
              =================================================== */}
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Please review your order before completing payment.
              </p>

              {isUSDT ? (
                <>
                  <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700 text-sm">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">You receive</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">
                        {usdtAmount.toFixed(2)} USDT
                      </strong>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Rate</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">
                        {offer.currency || ""} {usdtRate.toLocaleString()} / USDT
                      </strong>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Subtotal</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">
                        {offer.currency || ""} {usdtSubtotal.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Network Fee</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">
                        {networkFee} {settlementAsset}
                      </strong>
                    </div>
                    <div className="flex justify-between px-3 py-2 bg-neutral-50 dark:bg-neutral-800">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">Total</span>
                      <strong className="text-green-600 dark:text-green-400">
                        {offer.currency || ""} {usdtGrandTotal.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Settlement Asset</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">{settlementAsset}</strong>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Seller</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">
                        {offer.sellerUsername || "—"}
                      </strong>
                    </div>
                  </div>

                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 text-xs text-blue-700 dark:text-blue-300">
                    This transaction settles instantly. Your wallet will be charged the trade
                    amount plus a <strong>{networkFee} {settlementAsset}</strong> network fee.
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700 text-sm">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Asset</span>
                      <strong className="text-neutral-900 dark:text-neutral-100">{offer.asset}</strong>
                    </div>

                    {isBTC ? (
                      <>
                        <div className="flex justify-between px-3 py-2">
                          <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                          <strong className="text-neutral-900 dark:text-neutral-100">
                            {formatBtc(genericAmount)} BTC
                          </strong>
                        </div>
                        <div className="flex justify-between px-3 py-2">
                          <span className="text-neutral-500 dark:text-neutral-400">≈ USD</span>
                          <strong className="text-neutral-900 dark:text-neutral-100">
                            ${derivedUsd}
                          </strong>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between px-3 py-2">
                          <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                          <strong className="text-neutral-900 dark:text-neutral-100">
                            {genericAmount} {offer.asset}
                          </strong>
                        </div>
                        <div className="flex justify-between px-3 py-2">
                          <span className="text-neutral-500 dark:text-neutral-400">Rate</span>
                          <strong className="text-neutral-900 dark:text-neutral-100">
                            {offer.rate}
                          </strong>
                        </div>
                        <div className="flex justify-between px-3 py-2 bg-neutral-50 dark:bg-neutral-900">
                          <span className="font-semibold text-neutral-700 dark:text-neutral-200">Total</span>
                          <strong className="text-green-600 dark:text-green-400">
                            {genericTotal.toFixed(2)}
                          </strong>
                        </div>
                      </>
                    )}

                    {requiresPayment && selectedPaymentType && (
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Payment Method</span>
                        <strong className="text-neutral-900 dark:text-neutral-100">
                          {selectedPaymentType}
                        </strong>
                      </div>
                    )}
                  </div>

                  {requiresPayment && selectedPayment && (
                    <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                      <strong className="block mb-1">Payment Details</strong>
                      {selectedPayment.details}
                    </div>
                  )}

                  {requiresPayment && globalInstruction && (
                    <div className="rounded-lg bg-yellow-50 p-3 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                      <strong className="block mb-1">Payment Instruction</strong>
                      {globalInstruction}
                    </div>
                  )}
                </>
              )}

              <div className="rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950 dark:border-amber-800 px-3 py-2.5">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Double-check the details above. This action cannot be undone once submitted.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackToForm}
                  disabled={creatingOrder}
                  className="
                    flex-1 rounded-lg border py-2.5 text-sm font-semibold
                    border-neutral-300 text-neutral-700
                    hover:bg-neutral-50
                    dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700
                    disabled:opacity-40 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={isUSDT ? handleConfirmUsdt : handleConfirmGeneric}
                  disabled={creatingOrder}
                  className="
                    flex-1 rounded-lg bg-green-600 py-2.5 text-sm font-semibold text-white
                    hover:bg-green-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  {creatingOrder ? "Processing…" : "Confirm & Pay"}
                </button>
              </div>
            </>
          ) : (

          /* ===================================================
              FORM SCREEN
          =================================================== */
          isUSDT ? (
            <>
              {/* ── Amount input ── */}
              <div>
                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1">
                  Amount (USDT)
                </label>
              <input
  type="text"
  inputMode="decimal"
  value={buyAmount}
  onChange={(e) => {
    const val = e.target.value;

    if (
      val === "" ||
      /^\d*\.?\d{0,2}$/.test(val)
    ) {
      setBuyAmount(val);
    }
  }}
  onBlur={() => {
    if (
      buyAmount !== "" &&
      !Number.isNaN(Number(buyAmount))
    ) {
      setBuyAmount(
        Number(buyAmount).toFixed(2)
      );
    }
  }}
  placeholder={`${offer.minLimit} – ${offer.maxLimit}`}
  required
  aria-invalid={Boolean(amountError)}
  className={`w-full rounded-lg border px-3 py-2 text-sm
    bg-white text-neutral-900
    focus:ring-2
    dark:bg-neutral-900 dark:text-white
    ${amountError
      ? "border-red-500 focus:ring-red-500 dark:border-red-500"
      : "border-neutral-300 focus:ring-blue-500 dark:border-neutral-600"}`}
/>
                {amountError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {amountError}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                    Limits: {offer.minLimit} – {offer.maxLimit} USDT
                  </p>
                )}
              </div>

              {/* ── Order summary — always visible ── */}
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700 text-sm">
                <div className="flex justify-between px-3 py-2">
                  <span className="text-neutral-500 dark:text-neutral-400">You receive</span>
                  <strong className="text-neutral-900 dark:text-neutral-100">
                    {usdtAmount > 0 ? `${usdtAmount.toFixed(2)} USDT` : "—"}
                  </strong>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-neutral-500 dark:text-neutral-400">Rate</span>
                  <strong className="text-neutral-900 dark:text-neutral-100">
                    {offer.currency || ""} {usdtRate.toLocaleString()} / USDT
                  </strong>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-neutral-500 dark:text-neutral-400">
                    Subtotal
                  </span>

                  <strong className="text-neutral-900 dark:text-neutral-100">
                    {usdtAmount > 0
                      ? `${offer.currency || ""} ${usdtSubtotal.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                        })}`
                      : "—"}
                  </strong>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-neutral-500 dark:text-neutral-400">
                    Network Fee
                  </span>

                  <strong className="text-neutral-900 dark:text-neutral-100">
                    {networkFee} {settlementAsset}
                  </strong>
                </div>
                <div className="flex justify-between px-3 py-2 bg-neutral-50 dark:bg-neutral-800">
                  <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                    Total
                  </span>

                  <strong className="text-green-600 dark:text-green-400">
                    {usdtAmount > 0
                      ? `${offer.currency || ""} ${usdtGrandTotal.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}`
                      : "—"}
                  </strong>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-neutral-500 dark:text-neutral-400">Seller</span>
                  <strong className="text-neutral-900 dark:text-neutral-100">
                    {offer.sellerUsername || "—"}
                  </strong>
                </div>
              </div>

              {/* ── Settlement Asset ── */}

              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-2">
                  Settlement Asset
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSettlementAsset("USWD")}
                    className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                      settlementAsset === "USWD"
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-neutral-300 bg-white text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
                    }`}
                  >
                    USWD
                  </button>

                  <button
                    type="button"
                    onClick={() => setSettlementAsset("GHSX")}
                    className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                      settlementAsset === "GHSX"
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-neutral-300 bg-white text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
                    }`}
                  >
                    GHSX
                  </button>
                </div>

                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Network fee: {networkFee} {settlementAsset}
                </p>
              </div>

              {/* ── Notice ── */}
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 text-xs text-blue-700 dark:text-blue-300">
                This transaction settles instantly.
                Your wallet will be charged the trade amount plus a{" "}
                <strong>
                  {networkFee} {settlementAsset}
                </strong>{" "}
                network fee. USDT is credited immediately after payment.
              </div>

              {/* ── Action ── */}
              <form onSubmit={handlePreviewUsdt}>
                <button
                  type="submit"
                  disabled={!canPreviewUsdt}
                  className="
                    w-full rounded-lg bg-green-600 py-2.5 text-sm font-semibold text-white
                    hover:bg-green-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  Review Order
                </button>
              </form>
            </>
          ) : (
            <>
              {/* ===== NON-USDT FLOW (unchanged) ===== */}

              {/* ── Offer summary ── */}
              <div className="rounded-lg bg-neutral-50 p-3 text-sm dark:bg-neutral-900">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Asset</span>
                  <strong>{offer.asset}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Price</span>
                  <strong>{offer.rate}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Limits</span>
                  <strong>
                    {formatBtc(offer.minLimit)} – {formatBtc(offer.maxLimit)}
                  </strong>
                </div>
              </div>

              {/* ================= FORM ================= */}
              <form onSubmit={handlePreviewGeneric} className="space-y-4">
                {/* ================= BTC ================= */}
                {isBTC && (
                  <>
                    <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                      Amount
                    </label>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        placeholder="BTC"
                        value={btcValue}
                        onChange={(e) => {
                          setIsTypingUsd(false);
                          setBuyAmount(e.target.value);
                        }}
                        onBlur={() => setBuyAmount(normalizeBtc(btcValue))}
                        inputMode="decimal"
                        aria-invalid={Boolean(amountError)}
                        className={`rounded-lg border px-3 py-2 text-sm
                          bg-white text-neutral-900
                          focus:ring-2
                          dark:bg-neutral-900 dark:text-white
                          ${amountError
                            ? "border-red-500 focus:ring-red-500 dark:border-red-500"
                            : "border-neutral-300 focus:ring-blue-500 dark:border-neutral-600"}`}
                      />

                      <input
                        placeholder="USD"
                        value={isTypingUsd ? usdInput : derivedUsd}
                        onChange={(e) => {
                          if (!btcUsd) return;
                          const raw = e.target.value;
                          setIsTypingUsd(true);
                          setUsdInput(raw);
                          const usd = Number(raw);
                          if (!Number.isNaN(usd)) {
                            setBuyAmount(
                              Number((usd / btcUsd).toFixed(BTC_DECIMALS))
                            );
                          }
                        }}
                        onBlur={() => {
                          setIsTypingUsd(false);
                          if (!btcUsd) return;
                          const usd = normalizeUsd(usdInput);
                          setBuyAmount(normalizeBtc(usd / btcUsd));
                          setUsdInput("");
                        }}
                        inputMode="decimal"
                        className="rounded-lg border px-3 py-2 text-sm
                          border-neutral-300 bg-white text-neutral-900
                          focus:ring-2 focus:ring-blue-500
                          dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
                      />
                    </div>

                    {amountError ? (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {amountError}
                      </p>
                    ) : (
                      <div className="rounded-md bg-neutral-50 p-2 text-xs dark:bg-neutral-900">
                        {!btcUsd
                          ? "Fetching BTC price…"
                          : `${btcValue || 0} BTC ≈ $${derivedUsd}`}
                      </div>
                    )}
                  </>
                )}

                {/* ================= NON-BTC / NON-USDT ================= */}
                {!isBTC && (
                  <>
                    <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                      Amount
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={buyAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          setBuyAmount(val);
                        }
                      }}
                      placeholder="Enter amount"
                      required
                      aria-invalid={Boolean(amountError)}
                      className={`w-full rounded-lg border px-3 py-2 text-sm
                        bg-white text-neutral-900
                        focus:ring-2
                        dark:bg-neutral-900 dark:text-white
                        ${amountError
                          ? "border-red-500 focus:ring-red-500 dark:border-red-500"
                          : "border-neutral-300 focus:ring-blue-500 dark:border-neutral-600"}`}
                    />
                    {amountError && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {amountError}
                      </p>
                    )}

                    <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                      Total
                    </label>
                    <input
                      readOnly
                      value={
                        buyAmount && offer
                          ? (Number(buyAmount) * offer.rate).toFixed(2)
                          : "0.00"
                      }
                      className="w-full rounded-lg border px-3 py-2 text-sm
                        border-neutral-200 bg-neutral-100 text-neutral-700
                        dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                    />
                  </>
                )}

                {/* ================= PAYMENT METHOD (USWD only) ================= */}
                {requiresPayment && paymentDetails?.length > 0 && (
                  <>
                    <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                      Payment Method
                    </label>
                    <select
                      value={selectedPaymentType || ""}
                      onChange={(e) => setSelectedPaymentType(e.target.value)}
                      required
                      className="w-full rounded-lg border px-3 py-2 text-sm
                        border-neutral-300 bg-white text-neutral-900
                        focus:ring-2 focus:ring-blue-500
                        dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
                    >
                      <option value="" disabled>
                        Select payment method
                      </option>
                      {paymentDetails.map((p) => (
                        <option key={p.type} value={p.type}>
                          {p.type}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {/* ================= ACTION ================= */}
                <button
                  type="submit"
                  disabled={!canPreviewGeneric}
                  className="
                    w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white
                    hover:bg-blue-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  Review Order
                </button>
              </form>

              {/* ================= PAYMENT DETAILS (USWD only) ================= */}
              {requiresPayment && selectedPayment && (
                <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                  <strong className="block mb-1">Payment Details</strong>
                  {selectedPayment.details}
                </div>
              )}

              {requiresPayment && globalInstruction && (
                <div className="rounded-lg bg-yellow-50 p-3 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                  <strong className="block mb-1">Payment Instruction</strong>
                  {globalInstruction}
                </div>
              )}
            </>
          )
          )}
        </div>
      </div>
    </div>
  );
}